import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { AVATAR_SIZES } from 'ui/avatar';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { updateUserProfileRequest } from '../actions';
import {
  ACCEPTS_TYPES,
  MAX_IMAGE_SIZE,
  MIN_IMAGE_SIZE,
  EDIT_DETAILS_FORM_TEXT as TEXT,
} from '../constants';
import { UpdateUserProfileRequestPayload } from '../types';
import {
  ProfilePhotoSection,
  ProfilePhoto,
  FileInput,
  UploadButton,
  RemoveButton,
  ButtonsSection,
} from './Styled';

type Props = {
  avatarUrl: string;
  initFirstName: string;
  initLastName: string;
  onCancel: () => void;
};

export const EditDetailsForm = ({ initFirstName, initLastName, avatarUrl, onCancel }: Props) => {
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState(initFirstName);
  const [lastName, setLastName] = useState(initLastName);
  const [file, setFile] = useState<File | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const onDropAccepted = (files: File[]) => {
    const [newFile] = files;
    setFile(newFile);
    setIsDirty(true);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTS_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    minSize: MIN_IMAGE_SIZE,
    noClick: !!file,
    onDropAccepted,
  });

  const onRemovePhoto = () => {
    setFile(null);
    setIsDirty(true);
  };

  const onSave = () => {
    const payload: UpdateUserProfileRequestPayload = {
      firstName,
      lastName,
      shouldRemoveAvatarUrl: isDirty && !file,
    };
    if (file) payload.avatarUrl = file;
    dispatch(
      updateUserProfileRequest({
        callback: onCancel,
        payload,
      }),
    );
  };

  const imgSrc = file ? URL.createObjectURL(file) : avatarUrl;
  const uploadText = file ? (file as File).name : TEXT.PROFILE_PHOTO.UPLOAD_BUTTON;

  return (
    <div>
      <FormField labelText={TEXT.PROFILE_PHOTO.LABEL}>
        <ProfilePhotoSection>
          <ProfilePhoto
            firstName={initFirstName}
            lastName={initLastName}
            imgSrc={imgSrc}
            size={AVATAR_SIZES.XLARGE}
          ></ProfilePhoto>
          <UploadButton {...getRootProps()}>
            <SecondaryButton>{uploadText}</SecondaryButton>
            <FileInput type="file" hidden {...getInputProps()} />
          </UploadButton>
          {imgSrc && (
            <RemoveButton onClick={onRemovePhoto} size={BUTTON_SIZES.SMALL}>
              {TEXT.PROFILE_PHOTO.REMOVE_BUTTON}
            </RemoveButton>
          )}
        </ProfilePhotoSection>
      </FormField>
      <FormField
        errorText={TEXT.FIRST_NAME.ERROR}
        hasError={!firstName}
        inputId={TEXT.FIRST_NAME.ID}
        labelText={TEXT.FIRST_NAME.LABEL}
      >
        <InputText id={TEXT.FIRST_NAME.ID} value={firstName} onChange={setFirstName}></InputText>
      </FormField>
      <FormField
        errorText={TEXT.LAST_NAME.ERROR}
        hasError={!lastName}
        inputId={TEXT.LAST_NAME.ID}
        labelText={TEXT.LAST_NAME.LABEL}
      >
        <InputText id={TEXT.LAST_NAME.ID} value={lastName} onChange={setLastName}></InputText>
      </FormField>
      <ButtonsSection>
        <PrimaryButton
          id={TEXT.SAVE_BUTTON_ID}
          onClick={onSave}
          size={BUTTON_SIZES.LARGE}
          isDisabled={!firstName || !lastName}
        >
          {TEXT.SAVE_BUTTON}
        </PrimaryButton>
        <SecondaryButton onClick={onCancel} size={BUTTON_SIZES.LARGE}>
          {TEXT.CANCEL_BUTTON}
        </SecondaryButton>
      </ButtonsSection>
    </div>
  );
};
